import type { FormSchemaType } from '@/schema';
import { Question, RecapSection, InfoLine, FieldCheckbox } from '@workspace/core-ts/components';
import { SmartFormGlobal, useGlobalForm, useSmartFormGlobal } from '@workspace/core-ts/contexts';
import { useContent } from '@workspace/core-ts/hooks';
import { normalizeEuro } from '@workspace/utils';
import { VeloIcon, VaeIcon, CargoIcon } from '@/components/icons';
import recapGptContent from '@/content/recap-gpt';

function buildListeOptionsFromTarif(tarif: any): { CodeOption: string; Souscrite: boolean }[] {
    const formules = tarif?.ListeFormules;
    if (!formules?.length) return [];

    const garanties = formules[0].ListeGaranties || [];
    return garanties
        .filter((g: any) => g.TypeGarantie === 'Option' && g.Disponible === 'Option' && g.Souscrite === true)
        .map((g: any) => ({ CodeOption: g.CodeGarantie, Souscrite: true }));
}

export default function RecapGPT() {
    const methods = useSmartFormGlobal<FormSchemaType>();
    const { actions } = useGlobalForm();
    const r = useContent();

    const vehicule = methods.watch('DemandeTarif.Vehicule');
    const devoirIC = methods.watch('DemandeTarif.DevoirConseil.DevoirIC');
    const isAgentConv = methods.watch('DemandeDevis.TypeMER') === 'AgentConv';

    const getVehiculeType = () => {
        switch (vehicule?.GenreVehicule) {
            case 'VEL':
                return 'Vélo Classique';
            case 'VAE':
                return 'Vélo à Assistance Électrique';
            case 'VELC':
                return 'Vélo Cargo';
            default:
                return vehicule?.GenreVehicule || '-';
        }
    };

    const getVehiculeIcon = () => {
        switch (vehicule?.GenreVehicule) {
            case 'VAE':
                return VaeIcon;
            case 'VELC':
                return CargoIcon;
            default:
                return VeloIcon;
        }
    };

    const handleEdit = (targetStep: string) => {
        actions.setReturnToStep('recap-gpt');
        actions.goToStep(targetStep);
    };

    const handleSubmit = (data: any) => {
        const listeOptions = buildListeOptionsFromTarif(data.Tarif);
        if (listeOptions.length > 0) {
            data.DemandeTarif.ListeOptions = listeOptions;
        }

        return data;
    };

    return (
        <SmartFormGlobal
            {...methods}
            className="mx-auto max-w-xl"
            onSubmit={handleSubmit}
        >
            <Question disabledAutoNext>
                <Question.Header>
                    <Question.Title>{r(recapGptContent.mainTitle)}</Question.Title>
                    <Question.Description>{r(isAgentConv ? recapGptContent.mainDescriptionAgent : recapGptContent.mainDescription)}</Question.Description>
                </Question.Header>

                <div className="space-y-4 w-full">
                    <RecapSection
                        icon={getVehiculeIcon()}
                        title={r(recapGptContent.vehiculeTitle)}
                        onEdit={() => handleEdit('votre-velo')}
                    >
                        <InfoLine label="Type" value={getVehiculeType()} />
                        <InfoLine label="Marque / Désignation" value={vehicule?.Designation} />
                        <InfoLine
                            label="Valeur"
                            value={vehicule?.ValeurVehicule ? normalizeEuro(vehicule.ValeurVehicule) : undefined}
                        />
                        <InfoLine label="Date d'achat" value={vehicule?.DateMEC} />
                        <InfoLine
                            label="Protection corporelle"
                            value={String(devoirIC) === '1' ? 'Oui' : String(devoirIC) === '0' ? 'Non' : '-'}
                        />
                    </RecapSection>

                    <FieldCheckbox
                        name="gptConsent"
                        label={r(isAgentConv ? recapGptContent.consentLabelAgent : recapGptContent.consentLabel)}
                        validation={{ required: 'Vous devez accepter pour continuer' }}
                    />
                </div>
            </Question>
        </SmartFormGlobal>
    );
}
